import React, { useState, useEffect } from 'react';
import { useChat } from '../../../hooks/useChat';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const propertyData = {
  address: '42 Harbour View Terrace, Mosman NSW 2088',
  listingPrice: 2800000,
  fairValue: 2750000,
  bedrooms: 4,
  bathrooms: 3,
  parking: 2,
  landSize: 620,
  daysOnMarket: 47,
  suburbMedianDays: 32,
  suburb: 'Mosman',
  state: 'NSW',
  priceGrowth12m: 8.4,
  suburbMedianPrice: 2650000,
  comparableSalesCount: 47,
  pricePerSqm: 4516,
};

const SYSTEM_PROMPT = `You are an expert Australian real estate analyst with deep knowledge of property markets, valuation methodologies, and investment analysis. Provide detailed, data-driven insights in a structured format. Always be specific with numbers and percentages. Format your response as JSON.`;

function buildPrompt(data) {
  return `Analyse this Australian property and provide comprehensive market insights:

Property: ${data.address}
Listing Price: $${data.listingPrice.toLocaleString()}
AI Fair Value Estimate: $${data.fairValue.toLocaleString()}
Bedrooms: ${data.bedrooms} | Bathrooms: ${data.bathrooms} | Parking: ${data.parking}
Land Size: ${data.landSize}sqm
Days on Market: ${data.daysOnMarket} (suburb median: ${data.suburbMedianDays} days)
Suburb: ${data.suburb}, ${data.state}
12-Month Price Growth: ${data.priceGrowth12m}%
Suburb Median Price: $${data.suburbMedianPrice.toLocaleString()}
Comparable Sales Analysed: ${data.comparableSalesCount}
Price per sqm: $${data.pricePerSqm.toLocaleString()}

Provide a JSON response with exactly this structure:
{
  "marketInsights": {
    "summary": "2-3 sentence market overview",
    "marketCondition": "Seller's Market | Buyer's Market | Balanced Market",
    "demandLevel": "High | Medium | Low",
    "keyDrivers": ["driver1", "driver2", "driver3"]
  },
  "comparativeAnalysis": {
    "verdict": "Overpriced | Fairly Priced | Underpriced",
    "premiumOrDiscount": "+1.8% above fair value",
    "vsSuburbMedian": "+5.7% above suburb median",
    "negotiationRoom": "$50,000 - $100,000",
    "summary": "2-3 sentence CMA summary"
  },
  "pricePrediction": {
    "6monthOutlook": "$X,XXX,XXX - $X,XXX,XXX",
    "12monthOutlook": "$X,XXX,XXX - $X,XXX,XXX",
    "growthForecast": "X.X%",
    "confidence": "High | Medium | Low",
    "rationale": "2-3 sentence explanation"
  },
  "fairValueRecommendation": {
    "recommendedOfferRange": "$X,XXX,XXX - $X,XXX,XXX",
    "strongBuyBelow": "$X,XXX,XXX",
    "walkAwayAbove": "$X,XXX,XXX",
    "strategy": "2-3 sentence offer strategy",
    "timing": "Immediate | Wait 30 days | Wait 60+ days"
  }
}`;
}

const AIMarketInsights = () => {
  const [insights, setInsights] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (error) toast.error('Failed to generate AI insights. Please try again.');
  }, [error]);

  useEffect(() => {
    if (response && !isLoading) {
      try {
        const cleaned = response.replace(/```json\n?|```\n?/g, '').trim();
        const parsed = JSON.parse(cleaned);
        setInsights(parsed);
      } catch {
        toast.error('Could not parse AI response. Please regenerate.');
      }
    }
  }, [response, isLoading]);

  const handleGenerate = () => {
    setInsights(null);
    setHasGenerated(true);
    sendMessage([
      { role: 'system', content: SYSTEM_PROMPT },
      { role: 'user', content: buildPrompt(propertyData) }
    ], { max_completion_tokens: 1200 });
  };

  const verdictColor = (v) => {
    if (!v) return '#00D4FF';
    if (v.toLowerCase().includes('over')) return '#FF6666';
    if (v.toLowerCase().includes('under')) return '#00FF88';
    return '#FFAA00';
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)' }}>
            <Icon name="BrainCircuit" size={18} color="#00D4FF" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">AI Market Insights</h3>
            <p className="text-xs text-muted-foreground">Powered by OpenAI GPT-5</p>
          </div>
        </div>
        <button
          onClick={handleGenerate}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-60"
          style={{ background: isLoading ? 'rgba(0,212,255,0.05)' : 'rgba(0,212,255,0.12)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.3)' }}
        >
          <Icon name={isLoading ? 'Loader' : 'Sparkles'} size={14} color="#00D4FF" className={isLoading ? 'animate-spin' : ''} />
          {isLoading ? 'Generating...' : hasGenerated ? 'Regenerate' : 'Generate Insights'}
        </button>
      </div>

      {!hasGenerated && !isLoading && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)' }}>
            <Icon name="Sparkles" size={24} color="#00D4FF" />
          </div>
          <p className="text-foreground font-medium mb-1">Generate AI-Powered Market Insights</p>
          <p className="text-sm text-muted-foreground max-w-sm">Click "Generate Insights" to get a comprehensive market analysis, CMA, price predictions, and offer recommendations powered by OpenAI.</p>
        </div>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1,2,3,4].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-muted rounded w-1/3 mb-2"></div>
              <div className="h-3 bg-muted rounded w-full mb-1"></div>
              <div className="h-3 bg-muted rounded w-4/5"></div>
            </div>
          ))}
        </div>
      )}

      {insights && !isLoading && (
        <div className="space-y-5">
          {/* Market Insights */}
          <div className="rounded-xl p-4" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="TrendingUp" size={15} color="#00D4FF" />
              <span className="text-sm font-semibold text-foreground">Market Insights</span>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,212,255,0.15)', color: '#00D4FF' }}>{insights?.marketInsights?.marketCondition}</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">{insights?.marketInsights?.summary}</p>
            <div className="flex flex-wrap gap-2">
              {insights?.marketInsights?.keyDrivers?.map((d, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(0,212,255,0.08)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.2)' }}>{d}</span>
              ))}
            </div>
          </div>

          {/* Comparative Market Analysis */}
          <div className="rounded-xl p-4" style={{ background: 'rgba(255,170,0,0.05)', border: '1px solid rgba(255,170,0,0.15)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="BarChart3" size={15} color="#FFAA00" />
              <span className="text-sm font-semibold text-foreground">Comparative Market Analysis</span>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: `rgba(${verdictColor(insights?.comparativeAnalysis?.verdict) === '#FF6666' ? '255,102,102' : verdictColor(insights?.comparativeAnalysis?.verdict) === '#00FF88' ? '0,255,136' : '255,170,0'},0.15)`, color: verdictColor(insights?.comparativeAnalysis?.verdict) }}>{insights?.comparativeAnalysis?.verdict}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="rounded-lg p-3" style={{ background: 'rgba(255,170,0,0.08)' }}>
                <p className="text-xs text-muted-foreground mb-1">vs Fair Value</p>
                <p className="text-sm font-bold" style={{ color: '#FFAA00' }}>{insights?.comparativeAnalysis?.premiumOrDiscount}</p>
              </div>
              <div className="rounded-lg p-3" style={{ background: 'rgba(255,170,0,0.08)' }}>
                <p className="text-xs text-muted-foreground mb-1">Negotiation Room</p>
                <p className="text-sm font-bold" style={{ color: '#FFAA00' }}>{insights?.comparativeAnalysis?.negotiationRoom}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{insights?.comparativeAnalysis?.summary}</p>
          </div>

          {/* Price Prediction */}
          <div className="rounded-xl p-4" style={{ background: 'rgba(170,136,255,0.05)', border: '1px solid rgba(170,136,255,0.15)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="LineChart" size={15} color="#AA88FF" />
              <span className="text-sm font-semibold text-foreground">Price Predictions</span>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(170,136,255,0.15)', color: '#AA88FF' }}>Confidence: {insights?.pricePrediction?.confidence}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="rounded-lg p-3" style={{ background: 'rgba(170,136,255,0.08)' }}>
                <p className="text-xs text-muted-foreground mb-1">6-Month Outlook</p>
                <p className="text-sm font-bold" style={{ color: '#AA88FF' }}>{insights?.pricePrediction?.['6monthOutlook']}</p>
              </div>
              <div className="rounded-lg p-3" style={{ background: 'rgba(170,136,255,0.08)' }}>
                <p className="text-xs text-muted-foreground mb-1">12-Month Outlook</p>
                <p className="text-sm font-bold" style={{ color: '#AA88FF' }}>{insights?.pricePrediction?.['12monthOutlook']}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{insights?.pricePrediction?.rationale}</p>
          </div>

          {/* Fair Value Recommendation */}
          <div className="rounded-xl p-4" style={{ background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.15)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Target" size={15} color="#00FF88" />
              <span className="text-sm font-semibold text-foreground">Fair Value Recommendation</span>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,255,136,0.15)', color: '#00FF88' }}>{insights?.fairValueRecommendation?.timing}</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="rounded-lg p-3" style={{ background: 'rgba(0,255,136,0.08)' }}>
                <p className="text-xs text-muted-foreground mb-1">Offer Range</p>
                <p className="text-xs font-bold" style={{ color: '#00FF88' }}>{insights?.fairValueRecommendation?.recommendedOfferRange}</p>
              </div>
              <div className="rounded-lg p-3" style={{ background: 'rgba(0,255,136,0.08)' }}>
                <p className="text-xs text-muted-foreground mb-1">Strong Buy Below</p>
                <p className="text-xs font-bold" style={{ color: '#00FF88' }}>{insights?.fairValueRecommendation?.strongBuyBelow}</p>
              </div>
              <div className="rounded-lg p-3" style={{ background: 'rgba(255,68,68,0.08)' }}>
                <p className="text-xs text-muted-foreground mb-1">Walk Away Above</p>
                <p className="text-xs font-bold" style={{ color: '#FF6666' }}>{insights?.fairValueRecommendation?.walkAwayAbove}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{insights?.fairValueRecommendation?.strategy}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIMarketInsights;
